import { computed, DestroyRef, effect, inject, Injectable, isDevMode, signal } from '@angular/core';
import { TAB_DETECTION_CONFIG_TOKEN } from '@app/tokens/tab-detection-injection-token';
import { SnackbarService } from '@shared/components/esignatur-snackbar/snackbar.service';
import { SnackbarType } from '@shared/types/snackbar-type.enum';

/**
 * Represents a message used for communication related to browser tabs.
 *
 * The `TabMessage` type encompasses various messages such as pings, notifications of existing tabs,
 * heartbeat signals, and notification when a tab is closing. This type is structured as a union of
 * message objects, with each object having a specific `type` property and associated data.
 *
 */
type TabMessage =
  | { type: 'TAB_PING'; tabId: string; timestamp: number }
  | { type: 'TAB_EXISTS'; targetTabId: string; existingTabId: string }
  | { type: 'HEARTBEAT'; tabId: string; timestamp: number }
  | { type: 'TAB_CLOSING'; tabId: string };

@Injectable({
  providedIn: 'root',
})
export class TabDetection {
  readonly #config = inject(TAB_DETECTION_CONFIG_TOKEN);
  readonly #destroyRef = inject(DestroyRef);
  readonly #snackbarService = inject(SnackbarService);

  /** Unique identifier for this tab instance (set once, never changes) */
  readonly #tabId = signal<string>(crypto.randomUUID() + '-' + Date.now().toString());

  /** BroadcastChannel for cross-tab communication (imperative browser API, not a signal) */
  #channel: BroadcastChannel | null = null;

  /**
   * Set of active tab IDs tracked via heartbeat.
   * Tabs that don't send heartbeats are considered closed.
   * (Imperative data structure, not reactive state)
   */
  readonly #activeTabs = new Set<string>();

  /** Last heartbeat timestamp for each tab (imperative data structure) */
  readonly #lastHeartbeat = new Map<string, number>();

  /**
   * Tracks if this tab ever detected a duplicate.
   * Once set to true, prevents the continuous monitoring from clearing the duplicate flag.
   * This fixes the race condition when tabs refresh simultaneously.
   */
  #everDetectedDuplicate = signal<boolean>(false);

  /**
   * Timestamp when the service was initialized.
   * Used to prevent premature clearing of a duplicate flag during an initialization period.
   * (Regular property - value never changes, no reactive tracking needed)
   */
  readonly #initializationTimestamp = Date.now();

  // ===== Public Reactive State (Readonly Signals) =====

  /**
   * Private writable signal for duplicate tab state.
   * Only the service can mutate this.
   */
  #isDuplicateTabWritable = signal(false);

  /**
   * Private writable signal for monitoring state.
   * Only the service can mutate this.
   */
  #isMonitoringWritable = signal(false);

  /**
   * Whether this tab is a duplicate (another tab exists).
   * Readonly signal - external code can observe but not mutate.
   * @readonly
   */
  readonly isDuplicateTab = this.#isDuplicateTabWritable.asReadonly();

  /**
   * Number of active tabs currently open (including this tab).
   * @readonly
   */
  readonly activeTabCount = computed(() => {
    // Add 1 to include the current tab in the count
    return this.#activeTabs.size + 1;
  });

  /**
   * Whether the service is actively monitoring tabs.
   * Readonly signal - external code can observe but not mutate.
   * @readonly
   */
  readonly isMonitoring = this.#isMonitoringWritable.asReadonly();

  /**
   * Configuration for tab detection.
   * @readonly
   */
  readonly config = computed(() => this.#config);

  constructor() {
    // Skip initialization in development mode if configured
    if (isDevMode() && !this.#config.enableInDevelopment) {
      console.log('[TabDetection] Disabled in development mode');
      return;
    }
    this.#initialize();
    this.#setupHeartbeatEffect();
    this.#setupCleanup();
  }

  /**
   * Initializes the tab detection system.
   * Sets up BroadcastChannel and sends initial ping.
   */
  #initialize(): void {
    try {
      // Create a BroadcastChannel for communication
      this.#channel = new BroadcastChannel(this.#config.channelName!);
      this.#isMonitoringWritable.set(true);

      // Set up message listener
      this.#channel.onmessage = (event: MessageEvent<TabMessage>) => {
        this.#handleMessage(event.data);
      };

      // Send initial ping to announce presence
      this.#sendMessage({
        type: 'TAB_PING',
        tabId: this.#tabId(),
        timestamp: Date.now(),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to initialize tab detection';
      this.#snackbarService.showToaster(SnackbarType.ERROR, errorMessage);
      this.#isMonitoringWritable.set(false);
    }
  }

  /**
   * Handles incoming messages from other tabs.
   */
  #handleMessage(message: TabMessage): void {
    // Ignore messages from self
    if ('tabId' in message && message.tabId === this.#tabId()) {
      return;
    }

    switch (message.type) {
      case 'TAB_PING': {
        // Another tab just opened - respond that we exist
        this.#activeTabs.add(message.tabId);
        this.#lastHeartbeat.set(message.tabId, message.timestamp);

        // Send response indicating this tab exists
        this.#sendMessage({
          type: 'TAB_EXISTS',
          targetTabId: message.tabId,
          existingTabId: this.#tabId(),
        });
        break;
      }

      case 'TAB_EXISTS': {
        // Only respond if this message is for us
        if (message.targetTabId === this.#tabId()) {
          // CRITICAL: Add the existing tab to our active tabs tracking
          // This prevents the continuous monitoring from incorrectly clearing the duplicate flag
          this.#activeTabs.add(message.existingTabId);
          this.#lastHeartbeat.set(message.existingTabId, Date.now());

          this.#isDuplicateTabWritable.set(true);

          // Mark that we've ever detected a duplicate - prevents race condition clearing
          this.#everDetectedDuplicate.set(true);
        }
        break;
      }

      case 'HEARTBEAT': {
        // Update active tabs and last heartbeat time
        this.#activeTabs.add(message.tabId);
        this.#lastHeartbeat.set(message.tabId, message.timestamp);
        break;
      }

      case 'TAB_CLOSING': {
        // Remove the closed tab from tracking
        this.#activeTabs.delete(message.tabId);
        this.#lastHeartbeat.delete(message.tabId);
        break;
      }
    }
  }

  /**
   * Sends a message to all other tabs via BroadcastChannel.
   */
  #sendMessage(message: TabMessage): void {
    if (!this.#channel) return;

    try {
      this.#channel.postMessage(message);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to communicate with other tabs';
      this.#snackbarService.showToaster(SnackbarType.ERROR, errorMessage);
    }
  }

  /**
   * Sets up the heartbeat effect using Angular's effect() API.
   * The effect automatically manages the setInterval lifecycle and cleanup.
   * Runs when isMonitoring becomes true, cleans up when destroyed.
   */
  #setupHeartbeatEffect(): void {
    effect((onCleanup) => {
      // Guard: only run when monitoring is active
      if (!this.isMonitoring()) {
        return;
      }

      const HEARTBEAT_INTERVAL = 2000; // 2 seconds
      const HEARTBEAT_TIMEOUT = 5000; // 5 seconds (consider tab closed)

      // Start the heartbeat interval
      const intervalId = window.setInterval(() => {
        // Send heartbeat
        this.#sendMessage({
          type: 'HEARTBEAT',
          tabId: this.#tabId(),
          timestamp: Date.now(),
        });

        // Clean up stale tabs (no heartbeat for > 5 seconds)
        const now = Date.now();
        for (const [tabId, lastHeartbeat] of this.#lastHeartbeat.entries()) {
          if (now - lastHeartbeat > HEARTBEAT_TIMEOUT) {
            this.#activeTabs.delete(tabId);
            this.#lastHeartbeat.delete(tabId);
            console.log(`[TabDetection] Removed stale tab: ${tabId}`);
          }
        }

        // Continuously monitor: if only 1 tab remains, clear duplicate flag
        // CRITICAL: Multiple safety checks to prevent race condition bugs
        const timeSinceInit = Date.now() - this.#initializationTimestamp;
        const isInitPeriodOver = timeSinceInit > 3000; // 3-second initialization lock

        if (this.#activeTabs.size === 0 && this.isDuplicateTab()) {
          // Safety Check 1: Don't clear during an initialization period (prevents race condition)
          if (!isInitPeriodOver) {
            console.log(
              `[TabDetection] Skipping clear during initialization period (${timeSinceInit}ms elapsed)`
            );
            return;
          }

          // Safety Check 2: Don't clear if we ever actually detected a duplicate
          // (prevents clearing when tabs refresh simultaneously)
          if (this.#everDetectedDuplicate()) {
            console.log(
              '[TabDetection] Not clearing - duplicate was genuinely detected at some point'
            );
            return;
          }

          console.log('[TabDetection] No other tabs detected, clearing duplicate flag');
          this.#isDuplicateTabWritable.set(false);
        }
      }, HEARTBEAT_INTERVAL);

      // Register a cleanup function - called automatically when the effect is destroyed
      onCleanup(() => {
        clearInterval(intervalId);
        console.log('[TabDetection] Heartbeat interval cleaned up via effect');
      });
    });
  }

  /**
   * Sets up cleanup handlers for when the service is destroyed.
   */
  #setupCleanup(): void {
    this.#destroyRef.onDestroy(() => {
      console.log(`[TabDetection] Cleaning up tab: ${this.#tabId()}`);

      // Announce tab closing
      this.#sendMessage({
        type: 'TAB_CLOSING',
        tabId: this.#tabId(),
      });

      // Close channel
      if (this.#channel) {
        this.#channel.close();
        this.#channel = null;
      }

      // Note: No need to manually clear interval - the effect's onCleanup handles it!

      this.#isMonitoringWritable.set(false);
    });
  }

  /**
   * Gets the unique ID for this tab.
   */
  getTabId(): string {
    return this.#tabId();
  }
}
